import * as React from 'react';

import { cn } from './cn';
import { Icon } from './Icon';
import { Text } from './Typography';

// Closed list primitive — flush rows for menus, search results and dense
// pickers. Caller passes `items` (with `value`, `label`, optional
// `leadingIcon` / `trailingIcon` / `description`) and an `onSelect`
// handler that fires with the row's value when the row is clicked.
//
// Rows are flush (no per-row rounding, no inter-row gap). Caller wraps
// in their own rounded container if needed — typical popover pattern is
// `<div className="rounded-xl border">{<List … />}</div>`.

// Rows are vertically centred (`tw-items-center`) so both the leading
// and trailing icons sit on the row's mid-axis regardless of whether
// the description wraps onto a second line. Earlier the row was
// `items-start` with the leading icon nudged down by 2px to align with
// the title — that read fine for email-style rows where the title
// dominates, but in our menus / search-results pattern the icons are
// semantically "this row" not "this title", and the visual asymmetry
// (leading aligned with title, trailing centred) was distracting.
const ROW_CLASSNAME = 'tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus:tw-bg-muted hover:tw-bg-muted';

const renderIconNode = (icon, position) => (
    icon ? (
        <span className={cn(
            'tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center',
            position === 'trailing' ? 'tw-ml-auto' : null
        )}>
            <Icon icon={icon} size="small" tone="inherit" />
        </span>
    ) : null
);

const List = ({ items, onSelect }) => (
    <div className="tw-flex tw-flex-col">
        {items.map(item => (
            <button
                className={ROW_CLASSNAME}
                key={item.value}
                onClick={() => onSelect?.(item.value)}
                type="button"
            >
                {renderIconNode(item.leadingIcon, 'leading')}
                <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
                    <Text tone="inherit" variant="control">{item.label}</Text>
                    {item.description ? (
                        <Text tone="muted" variant="body-sm">{item.description}</Text>
                    ) : null}
                </span>
                {renderIconNode(item.trailingIcon, 'trailing')}
            </button>
        ))}
    </div>
);

export { List };
